import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { getApiUrl } from '../config/api.ts';
import { createLogger, toErrorMessage } from '@metron/shared';

const logger = createLogger('useDataLinkAccounts');

export interface DataLinkAccount {
  username: string;
  email: string;
  customerId: number;
  customerName?: string;
  allSensorsGroupId: number;
  userStatus: string;
  enabled: boolean;
  externalEnabled: boolean;
  createdAt: string;
  lastModifiedAt: string;
}

interface DataLinkAccountsResponse {
  accounts: DataLinkAccount[];
}

export interface ResetDataLinkCredentialsResponse {
  username: string;
  email: string;
  temporaryPassword: string;
  emailSent: boolean;
  emailError?: string;
}

export function useDataLinkAccounts() {
  const [accounts, setAccounts] = useState<DataLinkAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();
  // Only the first load blocks the page. A refetch must not flip isLoading,
  // because the page swaps its whole tree for a loading message and that
  // unmounts any open dialog along with its credentials.
  const hasLoadedRef = useRef(false);

  const fetchAccounts = async () => {
    if (!hasLoadedRef.current) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(getApiUrl('datalink/accounts'), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch DataLink accounts');
      }

      const data: DataLinkAccountsResponse = await response.json();
      setAccounts(data.accounts);
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to fetch DataLink accounts');
      setError(errorMessage);
      logger.error({ err }, 'Error fetching DataLink accounts');
    } finally {
      hasLoadedRef.current = true;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [getAccessToken]);

  const updateExternalAccess = useCallback(
    async (username: string, enabled: boolean) => {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(
        getApiUrl(`datalink/accounts/${encodeURIComponent(username)}/external-access`),
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ enabled }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to update external access');
      }

      // Optimistically update local state
      setAccounts((prev) =>
        prev.map((account) =>
          account.username === username ? { ...account, externalEnabled: enabled } : account
        )
      );

      return response.json();
    },
    [getAccessToken],
  );

  const resetCredentials = useCallback(
    async (username: string): Promise<ResetDataLinkCredentialsResponse> => {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(
        getApiUrl(`datalink/accounts/${encodeURIComponent(username)}/reset-auth`),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to reset credentials');
      }

      return response.json();
    },
    [getAccessToken],
  );

  const deleteAccount = useCallback(
    async (username: string, confirmEmail: string): Promise<void> => {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(
        getApiUrl(`datalink/accounts/${encodeURIComponent(username)}`),
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ confirmEmail }),
        },
      );

      if (!response.ok) {
        // A gateway/cold-start error can return a non-JSON body; don't let the
        // parse failure replace the real error message.
        let errorData: { error?: { message?: string } } = {};
        try {
          errorData = await response.json();
        } catch {
          // non-JSON error body
        }
        throw new Error(errorData.error?.message || 'Failed to delete account');
      }

      setAccounts((prev) => prev.filter((account) => account.username !== username));
    },
    [getAccessToken],
  );

  return {
    accounts,
    isLoading,
    error,
    refetch: fetchAccounts,
    updateExternalAccess,
    resetCredentials,
    deleteAccount,
  };
}
